import { FilterInput } from '@kinetic-ui/data-layer';
import { Badge, Box } from '@mui/material';
import { DownloadButton, DownloadButtonProps, FiltersButton } from 'components/buttons';
import { Search as SearchInput } from 'components/Search';
import { UpdateConfig } from 'components/Table';
import { memo, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Override } from 'types';
import {
  CombinedTableControlsProvider,
  CombinedTableControlsProviderProps,
  useCombinedTableControlsContext,
  useControlsDrawerContext,
  useSearchContext,
} from './contexts/context';
import { useFiltersContext } from './contexts/filtersContext';
import { ControlsDrawer } from './ControlsDrawer';
import { SavedFilterChips } from './SavedFilterChips';

const Search = () => {
  const { onSearchChange } = useCombinedTableControlsContext();
  return <SearchInput onChange={(e) => onSearchChange?.(e.target.value)} />;
};

const ControlsButton = () => {
  const { t } = useTranslation('common');
  const { open } = useControlsDrawerContext();
  const { filterInputs } = useFiltersContext();

  return (
    <Badge color='primary' badgeContent={filterInputs.length} variant='dot'>
      <FiltersButton title={t('combinedTableControlsButtonTitle')} onClick={open} />
    </Badge>
  );
};

const ExportButton = ({
  title,
  onClick,
  ...props
}: Override<
  DownloadButtonProps,
  {
    onClick: (values: {
      columns: UpdateConfig[];
      searchText: string;
      filters: FilterInput[];
    }) => void;
  }
>) => {
  const { t } = useTranslation('common');
  const { filterInputs } = useFiltersContext();
  const { columnsRef } = useCombinedTableControlsContext();
  const { searchText } = useSearchContext();

  const tooltip = title ?? t('export');
  return (
    <DownloadButton
      title={tooltip}
      {...props}
      onClick={() => {
        const values = {
          columns: columnsRef?.current ?? [],
          searchText: searchText,
          filters: filterInputs ?? [],
        };
        onClick(values);
      }}
    />
  );
};

const CardHeader = memo(
  ({ leftSlot, rightSlot }: { leftSlot?: ReactNode; rightSlot?: ReactNode }) => {
    const { columns, filters, search } = useCombinedTableControlsContext();

    const showDrawerButton = !!(columns || filters);
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', alignSelf: 'flex-start' }}>
            {leftSlot}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginLeft: 'auto' }}>
            {rightSlot}
            {search && (
              <Box>
                <Search />
              </Box>
            )}
            {showDrawerButton && (
              <Box>
                <ControlsButton />
              </Box>
            )}
          </Box>
        </Box>

        <SavedFilterChips />
      </Box>
    );
  }
);

type RootProps = CombinedTableControlsProviderProps<any>;
const Root = ({ children, ...props }: PropsWithChildren<RootProps>) => {
  return <CombinedTableControlsProvider {...props}>{children}</CombinedTableControlsProvider>;
};

export const CombinedTableControls = Object.assign(Root, {
  Drawer: ControlsDrawer,
  CardHeader: Object.assign(CardHeader, {
    ExportButton,
  }),
});
